import type { App, Directive, DirectiveBinding } from 'vue'
import { useAccessKyc } from '@/composables/useAccessKyc'
import { useAccountStore } from '@/stores/account'
import type { AccessKycOptions, DirectiveState } from '@/shared/common/types/accessKyc.type'
import { openAlert } from '@flash-global66/g-dialog-alert'
import { alertIncompleteOnboarding, alertPendingOnboarding, onButtonAlertOnb } from '@/shared/common/utils/alertOnboarding.util'

const elementMap = new WeakMap<HTMLElement, DirectiveState>()

const accessKycDirective: Directive = {
  mounted(el, binding) {
    updateKycAccess(el, binding)
  },
  updated(el, binding) {
    updateKycAccess(el, binding)
  },
  beforeUnmount(el) {
    cleanupKycElement(el)
  }
}

function updateKycAccess(el: HTMLElement, binding: DirectiveBinding<AccessKycOptions>) {
  const { value, modifiers } = binding
  const { hasAccess } = useAccessKyc({
    user: value?.user,
    company: value?.company,
    compliance: value?.compliance
  })
  
  hasAccess.value ? enableElement(el, modifiers as Record<string, boolean>) : disableElement(el, modifiers as Record<string, boolean>)
}

function disableElement(el: HTMLElement, modifiers: Record<string, boolean>) {
  function handleRestrictedClick(event: Event) {
    event.preventDefault()
    event.stopImmediatePropagation()
    showKycAlert()
  }

  const state: DirectiveState = {
    originalParent: null,
    anchor: null,
    vueHandlers: [],
    originalDisplay: el.style.display
  }

  if (modifiers.hide) {
    // Modo v-if: eliminar del DOM
    const comment = document.createComment('kyc-restricted')
    state.originalParent = el.parentNode
    state.anchor = comment
    state.originalParent?.replaceChild(comment, el)
  } else if (modifiers.keep) {
    // Modo v-show: ocultar visualmente
    el.style.display = 'none'
  } else {
    // Comportamiento original: deshabilitar con alerta
    state.vueHandlers = [...((el as any)._vei?.onClick?.fns || [])]

    el.classList.add('el-kyc-restricted')
    el.addEventListener('click', handleRestrictedClick, { capture: true })
    state.clickHandler = handleRestrictedClick
    
    if ((el as any)._vei?.onClick) {
      (el as any)._vei.onClick = undefined
    }
  }

  elementMap.set(el, state)
}
function enableElement(el: HTMLElement, modifiers: Record<string, boolean>) {
  const state = elementMap.get(el)
  if (!state) return

  if (modifiers.hide) {
    // Restaurar elemento oculto
    state.originalParent?.replaceChild(el, state.anchor!)
  } else if (modifiers.keep) {
    // Restaurar visibilidad
    el.style.display = state.originalDisplay
  } else {
    // Restaurar comportamiento normal
    el.classList.remove('el-kyc-restricted')
    if (state.clickHandler) {
      el.removeEventListener('click', state.clickHandler, { capture: true })
    }
    
    if (state.vueHandlers.length > 0) {
      (el as any)._vei = (el as any)._vei || {}
      ;(el as any)._vei.onClick = {
        value: (...args: any[]) => state.vueHandlers.forEach(fn => fn(...args)),
        fns: state.vueHandlers
      }
    }
  }

  elementMap.delete(el)
}
function cleanupKycElement(el: HTMLElement) {
  const state = elementMap.get(el)
  if (state) {
    if (state.anchor) {
      state.originalParent?.replaceChild(el, state.anchor)
    }
    elementMap.delete(el)
  }
}

async function showKycAlert() {
  const account = useAccountStore();
  
  if (account.isKycCompanyPending || account.isKycUserProcess) {
    const {isPrimary, isSecondary} = await openAlert(alertPendingOnboarding);
    onButtonAlertOnb(account.kycStatus, isPrimary, isSecondary);
    return;
  }

  const {isPrimary, isSecondary} = await openAlert(alertIncompleteOnboarding);
  onButtonAlertOnb(account.kycStatus, isPrimary, isSecondary);
}

export function registerAccessKycDirective(app: App) {
  app.directive('kyc', accessKycDirective)
}